import React from "react";
import swal from "@sweetalert/with-react";

export const validUploadedTypes = ["image/png"];
export const REQUIRED_WIDTH = 348;
export const REQUIRED_HEIGHT = 348;

export const validateImageDimensions = (
    file: File,
    width: number = REQUIRED_WIDTH,
    height: number = REQUIRED_HEIGHT
): Promise<boolean> => {
    return new Promise((resolve) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(img.width === width && img.height === height);
        };

        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(false);
        };

        img.src = objectUrl;
    });
};

export interface QRCodeHandlerDeps {
    t: (key: string) => string;
    setDisplayImage: (value: string) => void;
    setQrCodeImage: (value: string) => void;
    setIsLoading: (value: boolean) => void;
}

export const createHandleFileChange = (deps: QRCodeHandlerDeps) => {
    return async (e: React.ChangeEvent<HTMLInputElement>) => {
        const { t, setDisplayImage, setQrCodeImage, setIsLoading } = deps;
        const fileTarget = e.target?.files;
        const apiUrl = `${process.env.REACT_APP_API_V2_URL}/upload/qr`;

        if (fileTarget !== null && fileTarget.length > 0) {
            const file = fileTarget[0];

            if (!validUploadedTypes.includes(file.type)) {
                return swal({
                    icon: "error",
                    content: <p>{t("Only PNG files are accepted")}!</p>,
                    buttons: false,
                    timer: 2000,
                });
            }

            const isValidDimensions = await validateImageDimensions(file);
            if (!isValidDimensions) {
                return swal({
                    icon: "error",
                    content: (
                        <p>{t("Image dimensions must be 348 × 348 pixels")}!</p>
                    ),
                    buttons: false,
                    timer: 2000,
                });
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setDisplayImage(reader.result as any);
            };
            reader.readAsDataURL(file);

            try {
                const formData = new FormData();
                formData.append("file", file, file.name);

                const response = await fetch(apiUrl, {
                    body: formData,
                    method: "PUT",
                });
                const result: any = await response.json();

                if (result.error) {
                    throw new Error(result.message);
                }

                setQrCodeImage(result.data);
                setIsLoading(false);

                swal({
                    icon: "success",
                    content: <p>{t("QR code loaded successfully.")}</p>,
                    buttons: false,
                    timer: 2000,
                });

                setTimeout(() => {
                    setDisplayImage(result.data);
                }, 3000);
            } catch (error: any) {
                console.error("Upload failed:", error);
                setIsLoading(false);

                swal({
                    icon: "error",
                    content: <p>{t(error.message)}</p>,
                    buttons: false,
                    timer: 2000,
                });
            }
        }
    };
};

export const createHandleDrop = (deps: QRCodeHandlerDeps) => {
    return async (e: React.DragEvent<HTMLDivElement>) => {
        const { t, setDisplayImage, setQrCodeImage, setIsLoading } = deps;
        e.preventDefault();
        e.stopPropagation();

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];
            const apiUrl = `${process.env.REACT_APP_API_V2_URL}/upload/qr`;

            if (!validUploadedTypes.includes(file.type)) {
                return swal({
                    icon: "error",
                    content: <p>{t("Only PNG files are accepted")}!</p>,
                    buttons: false,
                    timer: 2000,
                });
            }

            const isValidDimensions = await validateImageDimensions(file);
            if (!isValidDimensions) {
                return swal({
                    icon: "error",
                    content: (
                        <p>{t("Image dimensions must be 348 × 348 pixels")}!</p>
                    ),
                    buttons: false,
                    timer: 2000,
                });
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setDisplayImage(reader.result as any);
            };
            reader.readAsDataURL(file);

            try {
                const formData = new FormData();
                formData.append("file", file, file.name);

                const response = await fetch(apiUrl, {
                    body: formData,
                    method: "PUT",
                });
                const result: any = await response.json();

                if (result.error) {
                    throw new Error(result.message);
                }

                setQrCodeImage(result.data);
                setIsLoading(false);

                swal({
                    icon: "success",
                    content: <p>{t("QR code loaded successfully.")}</p>,
                    buttons: false,
                    timer: 2000,
                });

                setTimeout(() => {
                    setDisplayImage(result.data);
                }, 3000);
            } catch (error: any) {
                console.error("Upload failed:", error);
                setIsLoading(false);

                swal({
                    icon: "error",
                    content: <p>{t(error.message)}</p>,
                    buttons: false,
                    timer: 2000,
                });
            }
        }
    };
};

export const createHandleDeleteImage = (
    setQrCodeImage: (value: string) => void
) => {
    return () => {
        setQrCodeImage("");
    };
};
